import { FunctionComponent, JSX } from 'react';
import { useClientData } from '@/providers/ClientData.provider';
import styles from '../ScheduledTransactions.module.scss';
// import ApprovalPanel from '../../../UI/ApprovalPanel/ApprovalPanel';
// import { useApprovalWorkflow } from '@/providers/ApprovalWorkflow.provider';
import { useTranslation } from 'react-i18next';
import { useI18nLabels } from '@/utils/useI18nLabels';

import {
  Box,
  SubTitle,
  Table,
  Badge,
  formattedCurrency,
  formatMaskedAccount,
  capitalizeFirstLetterOfWords,
  TableProps,
  MergedScheduledPayment,
  GetColorStatus,
} from 'vopay';

type TablePropsHandlers = {
  scheduleDetails: () => TableProps;
  accountHolderDetails: () => TableProps;
  accountDetails: () => TableProps;
};

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
  visible?: boolean;
};

type TransactionOverviewProps = {
  transaction: MergedScheduledPayment;
};

const ScheduledTransactionOverview: FunctionComponent<TransactionOverviewProps> = ({ transaction }) => {
  const { response } = useClientData();
  const { t } = useTranslation('translations', { keyPrefix: 'ScheduledTransactions.Details' });
  const { frequencyLabelMapper, paymentTypeLabelMapper, scheduledPaymentStatusLabelMapper } = useI18nLabels();

  return (
    <Box flx="col" width="50%" mt="md">
      {/* /scheduled-payment' has no 'pending approval' status, but we are keeping it until we renew the scheduled transaction page. */}
      {/* {transaction.Status === 'pending approval' && (
        <Box pb="lg" mb="sm">
          <ApprovalPanel
            title="Status: Pending Approval"
            description="Approvers can find these transaction(s) in their Approval Inbox"
          />
        </Box>
      )} */}
      <SubTitle subTitle={t('ScheduleDetailsSection.title')} />

      <Box mb="lg">
        <div className={styles.tableContainer}>
          <Table {...getTableProps('scheduleDetails')} />
        </div>
      </Box>

      <SubTitle subTitle={t('AccountHolderDetailsSection.title')} />

      <Box mb="lg">
        <div className={styles.tableContainer}>
          <Table {...getTableProps('accountHolderDetails')} />
        </div>
      </Box>

      <SubTitle subTitle={t('AccountDetailsSection.title')} />

      <Box mb="lg">
        <div className={styles.tableContainer}>
          <Table {...getTableProps('accountDetails')} />
        </div>
      </Box>

      {/* ErrorMessage detail isn't returned by /scheduled-payment, but we are keeping it until we renew the scheduled transaction page. */}
      {/* {errorMessage.length > 0 && (
        <>
          <SubTitle subTitle={t('FailedScheduledTransactionsSection.title')} />
          <Box mb="lg">
            <div className={styles.tableContainer}>
              <Table
                {...{
                  name: 'Errors-details',
                  variant: 'pair',
                  columns: {
                    detailKey: { header: 'Errors' },
                    detailValue: { header: '' },
                  },
                  rows: errorMessage.map((value) => ({
                    detailKey: value[0],
                    detailValue: value[1].ErrorMessage,
                  })),
                }}
              />
            </div>
          </Box>
        </>
      )} */}
    </Box>
  );

  function getTableProps(handler: keyof TablePropsHandlers): TableProps {
    const handlers: TablePropsHandlers = {
      scheduleDetails: () => {
        const accountDetailsPair: KeyValuePair[] = [
          {
            display: t('ScheduleDetailsSection.Rows.scheduleID'),
            value: transaction.ID || '',
            visible: true,
          },
          {
            display: t('ScheduleDetailsSection.Rows.Status.label'),
            value: (
              <Badge
                label={scheduledPaymentStatusLabelMapper(transaction.Status)}
                hasIcon
                color={GetColorStatus(transaction.Status || '')}
              />
            ),
            visible: true,
          },
          {
            display: t('ScheduleDetailsSection.Rows.ScheduleType.label'),
            value: t(`ScheduleDetailsSection.Rows.ScheduleType.Options.${transaction.ScheduleType}`, ''),
            visible: true,
          },
          {
            display: t('ScheduleDetailsSection.Rows.Frequency.label'),
            // frequencyLabelMapper also covers the legacy-only frequencies
            // ('bimonthly'/'single') that legacy records can carry in Repeats
            value: transaction.Repeats
              ? frequencyLabelMapper(transaction.Repeats) || capitalizeFirstLetterOfWords(transaction.Repeats)
              : '',
            visible: !!transaction.Repeats,
          },
          {
            display: t('ScheduleDetailsSection.Rows.numberOfPayments'),
            value: transaction.NumberOfPayments || '',
            visible: transaction.NumberOfPayments !== '',
          },
          {
            display: t('ScheduleDetailsSection.Rows.endDate', { defaultValue: 'End Date' }),
            value: transaction.ScheduleEndDate || '',
            visible: !!transaction.ScheduleEndDate,
          },
          {
            display: t('ScheduleDetailsSection.Rows.description'),
            value: transaction.Description || '',
            visible: transaction.Description !== '',
          },
          {
            display: t('ScheduleDetailsSection.Rows.Type.label'),
            value: paymentTypeLabelMapper(transaction.PaymentType),
            visible: true,
          },
          {
            display: t('ScheduleDetailsSection.Rows.amount'),
            value: `$${formattedCurrency(transaction.Amount || '')}`,
            visible: true,
          },
          {
            display: t('ScheduleDetailsSection.Rows.currency'),
            value: response.Country === 'CA' ? 'CAD' : 'USD',
            visible: true,
          },
        ];

        const scheduleDetails: TableProps = {
          name: 'scheduled-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('ScheduleDetailsSection.title') },
            detailValue: { header: '' },
          },
          rows: accountDetailsPair
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return scheduleDetails;
      },
      accountHolderDetails: () => {
        const clientDetailsPair: KeyValuePair[] = [
          {
            display: t('AccountHolderDetailsSection.Rows.name'),
            value: transaction.TokenData.FullName || '',
            visible: !!transaction.TokenData.FullName,
          },
        ];

        const accountHolderDetails: TableProps = {
          name: 'account-holder-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('AccountHolderDetailsSection.title') },
            detailValue: { header: '' },
          },
          rows: clientDetailsPair
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return accountHolderDetails;
      },
      accountDetails: () => {
        const clientDetailsPair: KeyValuePair[] = [
          {
            display: t('AccountDetailsSection.Rows.financialInstitutionNumber'),
            value: transaction.TokenData.Bank || '',
            visible: !!transaction.TokenData.Bank,
          },
          {
            display: t('AccountDetailsSection.Rows.branchTransitNumber'),
            value: transaction.TokenData.TransitNumber || '',
            visible: !!transaction.TokenData.TransitNumber,
          },
          {
            display: t('AccountDetailsSection.Rows.accountNumber'),
            value: formatMaskedAccount(transaction.TokenData.MaskedAccount || ''),
            visible: !!transaction.TokenData.MaskedAccount,
          },
          {
            display: t('AccountDetailsSection.Rows.creditCardNumber'),
            value: formatMaskedAccount(transaction.TokenData.CreditCardNumber || ''),
            visible: !!transaction.TokenData.CreditCardNumber,
          },
          {
            display: t('AccountDetailsSection.Rows.creditCardBrand'),
            value: transaction.TokenData.CreditCardBrand
              ? capitalizeFirstLetterOfWords(transaction.TokenData.CreditCardBrand)
              : '',
            visible: !!transaction.TokenData.CreditCardBrand,
          },
          {
            display: t('AccountDetailsSection.Rows.creditCardExpiry'),
            value:
              transaction.TokenData.CreditCardExpiryMonth && transaction.TokenData.CreditCardExpiryYear
                ? `${transaction.TokenData.CreditCardExpiryMonth}/${transaction.TokenData.CreditCardExpiryYear}`
                : '',
            visible: !!transaction.TokenData.CreditCardExpiryMonth && !!transaction.TokenData.CreditCardExpiryYear,
          },
        ];

        const AccountDetails: TableProps = {
          name: 'account-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('AccountDetailsSection.title') },
            detailValue: { header: '' },
          },
          rows: clientDetailsPair
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return AccountDetails;
      },
    };

    return handlers[handler]();
  }
};

export default ScheduledTransactionOverview;
